<template>
  <luma-chat
    v-if="ready"
    ref="luma"
    title="Chat with us"
    id="luma"
    start-minimized
    :websocket-url="websocketUrl"
    :rest-url="restUrl"
  />
</template>

<script>
import { mapState } from 'vuex'

const TAG = 'luma-chat'

function loadLumaScript (src) {
  if (window.__lumaChatLoader) return window.__lumaChatLoader

  window.__lumaChatLoader = new Promise((resolve, reject) => {
    if (window.customElements && customElements.get(TAG)) {
      resolve()
      return
    }

    const existing = document.querySelector('script[data-luma-chat]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('luma-chat.js failed')))
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.lumaChat = '1'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load ' + src))
    document.head.appendChild(script)
  })

  return window.__lumaChatLoader
}

export default {
  name: 'LumaChatWidget',
  props: {
    host: {
      type: String,
      default: 'sales-agent.luxbp.com'
    }
  },
  data () {
    return { ready: false }
  },
  computed: {
    ...mapState({
      token: (state) => state.user.token
    }),
    websocketUrl () {
      return 'wss://' + this.host + '/webchat/ws'
    },
    restUrl () {
      return 'https://' + this.host + '/webchat/message'
    },
    scriptSrc () {
      return 'https://' + this.host + '/widget/v1.0.0/luma-chat.js'
    }
  },
  watch: {
    token: 'applyToken'
  },
  async mounted () {
    try {
      await loadLumaScript(this.scriptSrc)
      if (window.customElements && customElements.whenDefined) {
        await customElements.whenDefined(TAG)
      }
      this.ready = true
      await this.$nextTick()
      this.applyToken()
    } catch (err) {
      console.error('[LumaChat]', err)
    }
  },
  methods: {
    applyToken () {
      const el = this.$refs.luma
      if (!el || typeof el.setAuthToken !== 'function') return
      // Empty token calls the widget's clearAuth() — needed on logout, not just refresh.
      el.setAuthToken(this.token || '')
    }
  }
}
</script>

<style scoped>
  luma-chat {
    --luma-primary: #111111;
    --luma-primary-contrast: #ffffff;
    --luma-font: brandon-grotesque, Arial, 'Helvetica Neue', Helvetica, sans-serif;
    --luma-width: 380px;
    --luma-height: 560px;
    --luma-right: 24px;
    --luma-bottom: 24px;
  }

  /* Chat launcher */
  luma-chat::part(launcher) {
    border-radius: 999px;
  }

  /* Main chat window */
  luma-chat::part(window) {
    border-radius: 14px;
  }

  /* Header */
  luma-chat::part(header) {
  }

  /* Header title */
  luma-chat::part(title) {
  }

  /* Connection status */
  luma-chat::part(connection-status) {
  }

  /* Minimize button */
  luma-chat::part(close-button) {
  }

  /* Message list */
  luma-chat::part(messages) {
  }

  /* Customer messages */
  luma-chat::part(message-user) {
  }

  /* Luma messages */
  luma-chat::part(message-assistant) {
  }

  /* Typing state */
  luma-chat::part(typing-indicator) {
  }

  /* Quick-action area */
  luma-chat::part(quick-actions) {
  }

  /* Quick-action buttons */
  luma-chat::part(quick-action) {
  }

  /* Backend suggestion buttons */
  luma-chat::part(suggestion) {
  }

  /* Input area */
  luma-chat::part(composer) {
  }

  /* Message field */
  luma-chat::part(input) {
  }

  /* Send button */
  luma-chat::part(send-button) {
  }
</style>